import React from 'react';
import * as Portal from '@radix-ui/react-portal';
import {Swiper, SwiperSlide} from 'swiper/react';
import {Autoplay} from 'swiper';
import 'swiper/swiper.min.css';
import {Link} from 'components/Link';

/**
 * Homepage promo modal — Cardo Special Summer Sale (2026-08-24 → 09-07).
 *
 * A five-poster carousel over a dark backdrop, advancing one slide per
 * second, with a close button, backdrop-click and Esc to dismiss. It
 * re-opens on each homepage visit (no persistence), matching the previous
 * campaign's behaviour.
 *
 * Self-expiring: after PROMO_END (2026-09-07 23:59 HKT) the component
 * renders nothing, so the homepage reverts on its own — no revert deploy
 * needed. The check runs client-side on every open, which also covers
 * CDN-cached HTML: the modal only ever mounts after hydration.
 */
const PROMO_END = Date.parse('2026-09-07T23:59:00+08:00');

const PROMO_LINK = '/collections/flash-sale';

const SLIDES = [
  {
    src: '/images/promo/cardo-summer-2026/01-cover.jpg',
    alt: 'Cardo Special Summer Sale 低至7折 2026.8.24-9.7',
  },
  {
    src: '/images/promo/cardo-summer-2026/02-spirit.jpg',
    alt: 'Cardo SPIRIT 單機$688起 SPIRIT HD 單機$1035起',
  },
  {
    src: '/images/promo/cardo-summer-2026/03-freecom.jpg',
    alt: 'Cardo FREECOM 2x 單機$1323起 FREECOM 4x 單機$1596起 創新低價',
  },
  {
    src: '/images/promo/cardo-summer-2026/04-accessories.jpg',
    alt: 'Cardo 配件優惠 2nd Helmet Kit、Audio Set、Edgephones 創新低價',
  },
  {
    src: '/images/promo/cardo-summer-2026/05-packtalk.jpg',
    alt: 'Cardo PACKTALK PRO/EDGE/NEO 單機$2224起 Mesh Boost',
  },
];

export default function HomePromoPopup() {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (Date.now() >= PROMO_END) return;
    // Small delay so it eases in after the homepage paints rather than
    // flashing during the initial load. Pops on every homepage visit.
    const t = setTimeout(() => setOpen(true), 500);
    return () => clearTimeout(t);
  }, []);

  const close = React.useCallback(() => {
    setOpen(false);
  }, []);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    // Prevent the page behind from scrolling while the modal is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, close]);

  if (!open) return null;

  return (
    <Portal.Root>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Cardo Special Summer Sale 低至7折"
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 p-4"
        onClick={close}
      >
        <div
          className="relative w-full max-w-[420px]"
          // Clicks on the carousel/button area shouldn't close the modal.
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            aria-label="關閉"
            onClick={close}
            className="absolute -right-3 -top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white text-black shadow-lg transition-opacity duration-200 hover:opacity-80"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.5 9.35849L1.85849 15L0 13.1415L5.64151 7.5L0 1.85849L1.85849 0L7.5 5.64151L13.1415 0L15 1.85849L9.35849 7.5L15 13.1415L13.1415 15L7.5 9.35849Z"
                fill="currentColor"
              />
            </svg>
          </button>
          <Swiper
            modules={[Autoplay]}
            slidesPerView={1}
            loop
            // 每秒轉一張。
            autoplay={{delay: 1000, disableOnInteraction: false}}
            className="overflow-hidden rounded-lg shadow-2xl"
          >
            {SLIDES.map((slide) => (
              <SwiperSlide key={slide.src}>
                <Link to={PROMO_LINK} onClick={close} className="block">
                  <img
                    src={slide.src}
                    alt={slide.alt}
                    width={1280}
                    height={1600}
                    className="max-h-[85vh] w-full object-contain"
                  />
                </Link>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </Portal.Root>
  );
}
